import React from 'react';
import { useTranslation } from 'react-i18next';
import { type Brand } from '../../types'; // Use the original Brand type directly

// Extend the original Brand type to include logoUrl - REMOVED
// interface Brand extends OriginalBrand {
//   logoUrl?: string;
// }

interface BrandAccessBadgeProps {
  brand: Brand; // Use the original (now updated) Brand type
  role: string;
  status: string;
}

// Define role styles consistently with RoleTag.tsx
interface RoleStyle {
  bgColor: string;
  borderColor: string;
  emoji: string;
}

const getRoleStyles = (role: string): RoleStyle => {
  switch (role?.toLowerCase()) {
    case 'admin':
      return { bgColor: 'bg-[#EFFDF4]', borderColor: 'border-green-700', emoji: '👑' };
    case 'manager':
      return { bgColor: 'bg-blue-100', borderColor: 'border-blue-700', emoji: '💼' };
    case 'reviewer':
      return { bgColor: 'bg-[#FEFCE8]', borderColor: 'border-yellow-700', emoji: '👀' };
    case 'writer':
      return { bgColor: 'bg-indigo-100', borderColor: 'border-indigo-700', emoji: '✍️' };
    default:
      return { bgColor: 'bg-gray-100', borderColor: 'border-gray-400', emoji: '👤' };
  }
};

// Define status indicator styles
const getStatusIndicatorStyle = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-green-500';
    case 'pending':
      return 'bg-yellow-500';
    case 'declined': // Added declined status
      return 'bg-red-500';
    default:
      return 'bg-gray-400'; // Default grey for unknown statuses
  }
};

export const BrandAccessBadge: React.FC<BrandAccessBadgeProps> = ({
  brand,
  role,
  status
}) => {
  const { t } = useTranslation();
  const roleStyles = getRoleStyles(role);
  const statusIndicatorStyle = getStatusIndicatorStyle(status);

  // Build the main badge classes: Use role bg, role border, and force dark text
  const badgeClasses = `px-3 py-1 rounded-full text-xs inline-flex items-center border ${roleStyles.bgColor} ${roleStyles.borderColor} text-gray-900`;
  
  // Tooltip text
  const tooltipText = `${t(`teamMembers.status.${status}`, status)} ${t(`teamMembers.roles.${role}`, role).toLowerCase()}`;

  return (
    <div className="group relative">
      <div className={badgeClasses}>
        {/* Status Indicator Dot */}
        <span className={`w-2 h-2 rounded-full mr-2 ${statusIndicatorStyle}`}></span>
        
        {/* Brand Logo/Icon */}
        {brand.logoUrl ? (
          <img 
            src={brand.logoUrl}
            alt={brand.name}
            className="w-4 h-4 rounded-full mr-1.5 object-contain"
          />
        ) : (
          <div className="w-4 h-4 rounded-full mr-1.5 bg-gray-300 flex items-center justify-center text-xs text-gray-600">
            {brand.name.charAt(0)}
          </div>
        )}
        {/* Brand Name */}
        <span className="font-medium mr-1.5">{brand.name}</span>
        {/* Role Emoji */}
        <span>{roleStyles.emoji}</span>
      </div>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
        <div className="capitalize">
          {tooltipText}
        </div>
        {/* Arrow */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};